import React, { useState } from 'react';
import {
  AddressBook, Bank, Buildings, DownloadSimple, Export, FileCsv, Funnel, MicrosoftExcelLogo, Receipt, SealCheck, Tag, Users, type Icon,
} from '@phosphor-icons/react';
import { ImmoButton, InnenKarte, KartenTitel, ListenPanel, SchalterZeile, SegmentedControl } from '../../../../components/immobilien/ui';
import { useNotification } from '../../../../contexts/NotificationContext';
import { exportCsv } from '../../../../lib/export/exportCsv';
import { exportXlsx } from '../../../../lib/export/exportXlsx';
import { EXPORT_GRUPPEN, exportKonfig, standardGruppen, type DlExportDaten, type ExportGruppe } from '../../../../lib/dienstleister/export';
import type { DlZeile } from '../../../../lib/dienstleister/liste';

const GRUPPEN_ICON: Record<ExportGruppe, Icon> = {
  stammdaten: AddressBook, kategorien: Tag, objekte: Buildings, nachweise: SealCheck, rechnungen: Receipt, bank: Bank,
};

type Format = 'xlsx' | 'csv';
type Umfang = 'ansicht' | 'alle';

/** Export (Handoff Dienstleister §7, Screen 07). */
export function ExportPanel({ ansicht, alle, daten, heute, onSchliessen }: {
  ansicht: readonly DlZeile[];
  alle: readonly DlZeile[];
  daten: DlExportDaten;
  heute: string;
  onSchliessen: () => void;
}) {
  const { showToast } = useNotification();
  const [format, setFormat] = useState<Format>('xlsx');
  const [umfang, setUmfang] = useState<Umfang>('ansicht');
  const [gruppen, setGruppen] = useState<ReadonlySet<ExportGruppe>>(standardGruppen);
  const zeilen = umfang === 'ansicht' ? ansicht : alle;
  const schalte = (g: ExportGruppe) => (an: boolean) => setGruppen(prev => {
    const n = new Set(prev);
    if (an) n.add(g); else n.delete(g);
    return n;
  });
  const exportieren = () => {
    try {
      const konfig = exportKonfig(zeilen, daten, gruppen, heute);
      if (format === 'csv') exportCsv(konfig); else exportXlsx(konfig);
      showToast({ type: 'success', message: `${konfig.fileName}.${format} mit ${konfig.data.length} Zeilen erstellt` });
      onSchliessen();
    } catch (e) {
      showToast({ type: 'error', message: `Export fehlgeschlagen: ${e instanceof Error ? e.message : 'Unbekannter Fehler'}` });
    }
  };
  return (
    <ListenPanel icon={Export} titel="Export" sub="Dienstleisterliste als Excel oder CSV" onSchliessen={onSchliessen}>
      <InnenKarte>
        <KartenTitel>Format &amp; Felder</KartenTitel>
        <SegmentedControl<Format> ariaLabel="Format" wert={format} onChange={setFormat} className="self-start"
          segmente={[{ key: 'xlsx', label: 'Excel (.xlsx)', icon: MicrosoftExcelLogo }, { key: 'csv', label: 'CSV (;)', icon: FileCsv }]} />
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 8 }}>
          {EXPORT_GRUPPEN.map(g => <SchalterZeile key={g.key} icon={GRUPPEN_ICON[g.key]} label={g.label} an={gruppen.has(g.key)} onChange={schalte(g.key)} />)}
        </div>
      </InnenKarte>
      <InnenKarte>
        <KartenTitel>Welche Dienstleister?</KartenTitel>
        <div className="flex flex-wrap items-center justify-between" style={{ gap: 10 }}>
          <SegmentedControl<Umfang> ariaLabel="Umfang" wert={umfang} onChange={setUmfang}
            segmente={[{ key: 'ansicht', label: 'Aktuelle Ansicht', icon: Funnel }, { key: 'alle', label: 'Alle', icon: Users }]} />
          <ImmoButton art="akzent" groesse={44} icon={DownloadSimple} onClick={exportieren} disabled={zeilen.length === 0 || gruppen.size === 0}>
            {`${zeilen.length} Dienstleister exportieren`}
          </ImmoButton>
        </div>
      </InnenKarte>
    </ListenPanel>
  );
}
